import { useContext } from 'react';
import FeatureBranch from '@/common/FeatureBranch';
import PageFooter from '@/common/PageFooter';
import PageHeader from '@/common/PageHeader';
import BackgroundOverlay from '@/home/BackgroundOverlay';
import InternalLinks from '@/home/InternalLinks';
import {
  FinanceTradePromoTiles,
  ValueInsurancePromoTiles
} from '@/home/PromoTiles';
import RecommendedCarousels from '@/home/RecommendedCarousels';
import HideOnNative from '~/components/common/HideOnNative';
import { useIsLoggedIn } from '~/context/common/UserContext';
import { useGetRecentSearches } from '~/context/home/RecentSearchesContext';
import RecentVehiclesContext from '~/context/home/RecentVehiclesContext';
import NativeError from '~/native/NativeError';
import AffordableCars from './AffordableCars';
import Hero from './Hero';
import {
  ExpandedSectionContent,
  SectionDark,
  SectionContent,
  SectionLight
} from './HomeContainer.styles';
import useIsInReturnUserExperiment from './hooks/useIsInReturnUserExperiment';
import Modals from './Modals';
import NewPriceBanner from './NewPriceBanner';
import OrderStatusCardsWrapper from './OrderStatusCards';
import PopularBodyStyles from './PopularBodyStyles';
import PopularModels from './PopularModels';
import CVTBanner from './PromoCVT/CVTBanner';
import RecentlyViewed from './RecentlyViewed';
import RecentSearches from './RecentSearches';
import SuggestedForYou from './SuggestedForYou';

export default function HomeContainer() {
  const isLoggedIn = useIsLoggedIn();
  const {
    recentVehiclesFromBff: { data: recentVehicleViews }
  } = useContext(RecentVehiclesContext);
  const recentSearches = useGetRecentSearches();

  const isReturningOrLoggedInUser =
    recentVehicleViews.length + recentSearches.length > 0 || isLoggedIn;

  const isInReturnUserExperiment = useIsInReturnUserExperiment();

  const shouldShowOnControlAndNonReturning =
    (isInReturnUserExperiment && !isReturningOrLoggedInUser) ||
    !isInReturnUserExperiment;

  return (
    <>
      <BackgroundOverlay />
      <HideOnNative>
        <PageHeader isLoggedIn={isLoggedIn} />
      </HideOnNative>
      <NativeError />
      <FeatureBranch appName="homepage" />
      <main>
        <Hero isReturningUser={isReturningOrLoggedInUser} />
        <SectionLight>
          <SectionContent
            className={
              isInReturnUserExperiment && isReturningOrLoggedInUser
                ? 'hidden'
                : ''
            }
          >
            {shouldShowOnControlAndNonReturning && <CVTBanner />}
            {!isReturningOrLoggedInUser && (
              <PopularBodyStyles
                isReturningOrLoggedInUser={isReturningOrLoggedInUser}
              />
            )}
          </SectionContent>

          {isReturningOrLoggedInUser && (
            <ExpandedSectionContent>
              {!isInReturnUserExperiment && <OrderStatusCardsWrapper />}
              <RecentlyViewed />
              {isInReturnUserExperiment ? (
                <>
                  <SuggestedForYou />
                  <RecentSearches recentSearches={recentSearches} />
                </>
              ) : (
                <>
                  <RecentSearches recentSearches={recentSearches} />
                  <SuggestedForYou />
                </>
              )}
            </ExpandedSectionContent>
          )}

          <ExpandedSectionContent>
            {shouldShowOnControlAndNonReturning && (
              <FinanceTradePromoTiles isNewUser={!isReturningOrLoggedInUser} />
            )}
            <NewPriceBanner />
          </ExpandedSectionContent>
        </SectionLight>

        <SectionDark>
          <ExpandedSectionContent>
            {isReturningOrLoggedInUser && (
              <PopularBodyStyles
                isReturningOrLoggedInUser={isReturningOrLoggedInUser}
              />
            )}
            <AffordableCars />
          </ExpandedSectionContent>
        </SectionDark>

        {isReturningOrLoggedInUser && (
          <SectionLight>
            <ExpandedSectionContent>
              <RecommendedCarousels />
            </ExpandedSectionContent>
          </SectionLight>
        )}

        <SectionDark>
          <ExpandedSectionContent>
            <ValueInsurancePromoTiles />
          </ExpandedSectionContent>
        </SectionDark>

        <SectionLight>
          <ExpandedSectionContent>
            <PopularModels />
          </ExpandedSectionContent>
          {!isReturningOrLoggedInUser && (
            <HideOnNative>
              <InternalLinks />
            </HideOnNative>
          )}
        </SectionLight>
      </main>
      <HideOnNative>
        <PageFooter />
      </HideOnNative>
      <Modals />
    </>
  );
}
